import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../../shared/storage/firebase';
import { LibraryItem } from './types';
import { ClassroomTeam, PortfolioEntry, visibleAiCreations, visiblePortfolioPhotos } from '../squads/classroom/types';
import { DeckArtifact, UniverseCommunity, UniverseCoreData, UniverseSquad, UniverseSquadData } from './universeTypes';
import { regionForLocation } from './universeRegions';

const text = (value: unknown) => typeof value === 'string' ? value.trim() : '';
const titleCase = (value: string) => value.replace(/[_-]+/g, ' ').replace(/\b\w/g, letter => letter.toUpperCase());

export function squadDisplayName(data: Record<string, any>): string {
  const curriculum = text(data.curriculumTitle) || titleCase(text(data.curriculumId)) || text(data.classroomName) || text(data.title) || 'Creative Squad';
  const dayValue = data.schedule?.dayOfWeek ?? data.dayOfWeek;
  const day = typeof dayValue === 'number'
    ? ['Sundays', 'Mondays', 'Tuesdays', 'Wednesdays', 'Thursdays', 'Fridays', 'Saturdays'][dayValue]
    : text(dayValue) ? `${titleCase(text(dayValue)).replace(/s$/, '')}s` : '';
  return [curriculum, day].filter(Boolean).join(' · ');
}

export async function loadUniverseCore(authenticated: boolean): Promise<UniverseCoreData> {
  const currentNames = authenticated ? null : httpsCallable<void, { names: { squadId: string; teamId: string; name: string; avatarUrl?: string }[] }>(getFunctions(), 'getPublishedUniverseTeamNames')()
    .then(response => response.data.names)
    .catch(error => { console.error('Could not load current public team names', error); return []; });
  const [locationSnap, squadSnap, cardSnap] = await Promise.all([
    getDocs(collection(db, 'publicLocations')),
    getDocs(collection(db, authenticated ? 'squads' : 'publicSquads')),
    getDocs(query(collection(db, 'libraryItems'), where('status', '==', 'published'))),
  ]);
  const locations = locationSnap.docs.map(doc => ({ id: doc.id, ...doc.data() } as Record<string, any> & { id: string }));
  const squads: UniverseSquad[] = squadSnap.docs.map(doc => {
    const data = doc.data();
    return {
      id: doc.id,
      partnerId: text(data.partnerId),
      locationId: text(data.locationId),
      operationalName: text(data.title) || text(data.classroomName) || 'Untitled squad',
      displayName: squadDisplayName(data),
      curriculumLabel: text(data.curriculumTitle) || titleCase(text(data.curriculumId)),
      season: text(data.term),
      raw: data,
    };
  });
  const cards = cardSnap.docs.map(doc => ({ id: doc.id, ...doc.data() } as LibraryItem));
  cards.forEach(card => {
    const squad = squads.find(squad => squad.id === card.squadId);
    card.curriculumLabel ||= squad?.curriculumLabel;
  });
  if (currentNames) {
    const names = new Map<string, { name: string; avatarUrl?: string }>();
    (await currentNames).forEach(item => names.set(`${item.squadId}/${item.teamId}`, item));
    cards.forEach(card => {
      if (card.squadId && card.teamId) {
        const team = names.get(`${card.squadId}/${card.teamId}`);
        card.teamName = team?.name || 'Overture team';
        card.teamLogoUrl = team?.avatarUrl || card.teamLogoUrl;
      }
    });
  }
  if (authenticated) {
    const pairs = new Map(cards.filter(card => card.squadId && card.teamId).map(card => [`${card.squadId}/${card.teamId}`, card]));
    await Promise.all(Array.from(pairs.values()).map(async card => {
      try {
        const team = await getDoc(doc(db, 'squads', card.squadId!, 'teams', card.teamId!));
        const logo = text(team.data()?.avatarUrl);
        cards.filter(item => item.squadId === card.squadId && item.teamId === card.teamId).forEach(item => { item.teamLogoUrl = logo || item.teamLogoUrl; });
      } catch (error) { console.warn('Could not load team logo', error); }
    }));
  }
  const publishedSquadIds = new Set(cards.map(card => card.squadId).filter((id): id is string => Boolean(id)));
  const visibleSquads = squads.filter(squad => publishedSquadIds.has(squad.id));
  const communityMap = new Map<string, UniverseCommunity>();
  visibleSquads.forEach(squad => {
    const location = locations.find(item => item.id === squad.locationId);
    const communityId = squad.partnerId || text(location?.partnerId) || squad.locationId;
    if (!communityId) return;
    const community = communityMap.get(communityId) || { id: communityId, name: text(location?.partnerName) || text(squad.raw.schoolName) || 'Overture Community', logoUrl: text(location?.logoUrl), locationIds: squad.locationId ? [squad.locationId] : [], squadIds: [], cardCount: 0 };
    community.squadIds.push(squad.id);
    const region = regionForLocation(location || {});
    if (!community.regionId || community.regionId === 'elsewhere') {
      community.regionId = region.id;
      community.regionName = region.name;
    }
    communityMap.set(communityId, community);
  });
  communityMap.forEach(community => { community.cardCount = cards.filter(card => card.squadId && community.squadIds.includes(card.squadId)).length; });
  return { communities: Array.from(communityMap.values()).filter(community => community.cardCount > 0).sort((a, b) => a.name.localeCompare(b.name)), squads: visibleSquads, cards };
}

export async function loadSquadData(squadId: string, authenticated: boolean, publishedCards: LibraryItem[]): Promise<UniverseSquadData> {
  const squadCards = publishedCards.filter(card => card.squadId === squadId);
  if (!authenticated) {
    const byTeam = new Map<string, ClassroomTeam>();
    squadCards.filter(card => card.teamId).forEach(card => byTeam.set(card.teamId!, { id: card.teamId!, squadId, name: card.teamName || 'Team', colorKey: 'slate', memberIds: [], archived: false, createdAt: 0, updatedAt: 0 }));
    return { teams: Array.from(byTeam.values()), entries: [] };
  }
  const teamsSnap = await getDocs(collection(db, 'squads', squadId, 'teams'));
  const publishedTeamIds = new Set(squadCards.map(card => card.teamId).filter(Boolean));
  return {
    teams: teamsSnap.docs.map(doc => ({ id: doc.id, squadId, ...doc.data() } as ClassroomTeam)).filter(team => !team.archived && publishedTeamIds.has(team.id)),
    entries: [],
  };
}

export function artifactsForTeam(teamId: string, entries: PortfolioEntry[], publishedCards: LibraryItem[]): DeckArtifact[] {
  const libraryArtifacts = publishedCards.filter(card => card.teamId === teamId).map(card => ({ id: `library-${card.id}`, title: card.title, mediaType: card.contentType, imageUrl: card.displayAssetUrl, description: card.description, curriculumLabel: '', libraryItem: card }));
  const publishedCreationIds = new Set(publishedCards.map(card => card.sourcePortfolioCreationId).filter(Boolean));
  const portfolioArtifacts = entries.filter(entry => entry.teamId === teamId).flatMap(entry => {
    const ai = visibleAiCreations(entry.aiVersions).filter(creation => !publishedCreationIds.has(creation.id)).map(creation => ({
      id: `portfolio-${entry.id}-${creation.id}`,
      title: creation.caption || creation.filename || entry.levelTitle || 'Team creation',
      mediaType: creation.type || 'creation',
      imageUrl: creation.posterUrl || creation.url || '',
      description: entry.teacherNote || '',
      curriculumLabel: entry.levelTitle || '',
    }));
    if (ai.length) return ai;
    return visiblePortfolioPhotos(entry.photos).slice(0, 1).map(photo => ({ id: `photo-${entry.id}-${photo.id}`, title: entry.levelTitle || 'Portfolio creation', mediaType: 'portfolio', imageUrl: photo.url, description: entry.teacherNote || '', curriculumLabel: entry.levelTitle || '' }));
  });
  return [...libraryArtifacts, ...portfolioArtifacts];
}
